import { createAuthClient } from 'better-auth/react';

export const authConfigured = import.meta.env.MODE !== 'test' && import.meta.env.VITE_AUTH_DISABLED !== 'true';
export const authClient = createAuthClient({ baseURL: typeof window === 'undefined' ? undefined : window.location.origin });

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

export type ApiFetchOptions = RequestInit & {
  timeoutMs?: number;
  maxAttempts?: number;
  retryStatuses?: readonly number[];
};

export async function apiFetch<T>(path: string, init?: ApiFetchOptions): Promise<T> {
  const { timeoutMs = 15_000, maxAttempts, retryStatuses = [502, 503, 504], ...requestInit } = init || {};
  const method = (requestInit.method || 'GET').toUpperCase();
  const attempts = Math.max(1, maxAttempts ?? (method === 'GET' ? 2 : 1));
  let response: Response | null = null;
  let lastError: unknown = null;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const timeout = AbortSignal.timeout(timeoutMs);
    const signal = requestInit.signal ? AbortSignal.any([requestInit.signal, timeout]) : timeout;
    try {
      response = await fetch(path, {
        ...requestInit,
        signal,
        credentials: 'include',
        headers: { ...(requestInit.body ? { 'Content-Type': 'application/json' } : {}), ...requestInit.headers }
      });
      if (!retryStatuses.includes(response.status) || attempt === attempts - 1) break;
    } catch (error) {
      lastError = error;
      if (attempt === attempts - 1) throw new ApiError(error instanceof DOMException && error.name === 'TimeoutError' ? '请求超时，请稍后重试。' : '网络请求失败，请检查连接。', 0);
    }
  }
  if (!response) throw lastError instanceof Error ? lastError : new ApiError('网络请求失败。', 0);
  const payload = await response.json().catch(() => ({})) as { error?: string } & T;
  if (!response.ok) throw new ApiError(payload.error || `请求失败（${response.status}）`, response.status);
  return payload;
}
